import type { ConversationRun } from '@axilon-apps/domains-ontology-ui-state'

import { cn } from '../../lib/utils'
import { ScrollArea } from '../ui/scroll-area'
import { ChatTurn } from './ChatTurn'
import { usePersistedConversation } from './usePersistedConversation'

interface PastConversationTurnsProps {
  conversationId: string
  /**
   * Hydrate the transcript again whenever this changes. Pass a polled job's
   * `updatedAt` to follow a run that is still writing turns; leave it unset
   * for a settled conversation, which loads once.
   */
  refreshKey?: string | number
  /** Show turns that are still running; off for lanes that render the live turn elsewhere. */
  includeRunning?: boolean
  emptyText: string
  loadingText: string
  loadErrorText: string
  className?: string
}

/**
 * Read-only rendering of a persisted conversation with the chat surface's
 * own turn component, so tool calls, chain-of-thought, and answers look the
 * same here as in an agent tab. Hydrates through the orchestrator slice
 * (hidden tab) so a transcript already loaded there is reused, not refetched.
 */
export function PastConversationTurns({
  conversationId,
  refreshKey,
  includeRunning = false,
  emptyText,
  loadingText,
  loadErrorText,
  className
}: PastConversationTurnsProps) {
  const { turns: loadedTurns, loadError } = usePersistedConversation(
    conversationId,
    refreshKey,
    loadErrorText
  )

  if (loadError) {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        {loadError}
      </div>
    )
  }

  const turns = (loadedTurns ?? []).filter(
    (turn: ConversationRun) => includeRunning || turn.status !== 'running'
  )
  if (loadedTurns === null) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 text-center text-xs text-muted-foreground">
        {loadingText}
      </div>
    )
  }
  if (turns.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-4 text-center text-xs text-muted-foreground">
        {emptyText}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm',
        className
      )}
    >
      <ScrollArea className="h-[36rem] [&>[data-slot=scroll-area-viewport]>div]:!block">
        <div className="flex flex-col gap-4 px-6 py-4">
          {turns.map((turn: ConversationRun) => (
            <ChatTurn key={turn.id} kind="past" turn={turn} />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
